import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { supabase } from '../../lib/supabase';

const DEFAULT_OFFLINE_MESSAGE = 'We are temporarily unavailable. Please check back soon!';

export default function Dashboard() {
     const [stats, setStats] = useState({
          upcoming: [],
          monthlyBookings: 0,
          monthlyRevenue: 0,
     });
     const [loading, setLoading] = useState(true);

     // Away-mode (store offline) toggle
     const [storeOffline, setStoreOffline] = useState(false);
     const [offlineMessage, setOfflineMessage] = useState(DEFAULT_OFFLINE_MESSAGE);
     const [savingStatus, setSavingStatus] = useState(false);
     const [statusError, setStatusError] = useState(null);
     const [statusSavedAt, setStatusSavedAt] = useState(null);

     useEffect(() => {
          fetchDashboardData();
          fetchStoreStatus();
     }, []);

     async function fetchStoreStatus() {
          const { data } = await supabase
               .from('admin_settings')
               .select('value')
               .eq('key', 'store_offline')
               .maybeSingle();
          if (data?.value) {
               setStoreOffline(!!data.value.enabled);
               setOfflineMessage(data.value.message || DEFAULT_OFFLINE_MESSAGE);
          }
     }

     async function saveStoreStatus(nextEnabled, nextMessage) {
          setSavingStatus(true);
          setStatusError(null);
          const { error } = await supabase
               .from('admin_settings')
               .upsert(
                    {
                         key: 'store_offline',
                         value: {
                              enabled: nextEnabled,
                              message: nextMessage || DEFAULT_OFFLINE_MESSAGE,
                         },
                    },
                    { onConflict: 'key' }
               );
          setSavingStatus(false);
          if (error) {
               setStatusError(error.message || 'Failed to save.');
               return;
          }
          setStatusSavedAt(new Date());
     }

     async function fetchDashboardData() {
          const today = format(new Date(), 'yyyy-MM-dd');
          const weekFromNow = format(addDays(new Date(), 7), 'yyyy-MM-dd');
          const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
          const monthEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');

          const [upcomingRes, monthlyRes] = await Promise.all([
               supabase
                    .from('bookings')
                    .select('*')
                    .eq('status', 'confirmed')
                    .gte('start_date', today)
                    .lte('start_date', weekFromNow)
                    .order('start_date'),
               supabase
                    .from('bookings')
                    .select('*')
                    .in('status', ['confirmed', 'completed'])
                    .gte('start_date', monthStart)
                    .lte('start_date', monthEnd),
          ]);

          const monthlyBookings = monthlyRes.data || [];
          const monthlyRevenue = monthlyBookings.reduce(
               (sum, b) => sum + (b.total_cents || 0),
               0
          );

          setStats({
               upcoming: upcomingRes.data || [],
               monthlyBookings: monthlyBookings.length,
               monthlyRevenue,
          });
          setLoading(false);
     }

     if (loading) {
          return <p className='text-darker/60'>Loading dashboard...</p>;
     }

     return (
          <div>
               <h1 className='text-darker text-2xl font-bold mb-6'>Dashboard</h1>

               {/* Stats Cards */}
               <div className='grid grid-cols-1 tablet:grid-cols-3 gap-4 mb-8'>
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <p className='text-darker/40 text-sm'>Upcoming (7 days)</p>
                         <p className='text-darker text-3xl font-bold mt-1'>
                              {stats.upcoming.length}
                         </p>
                    </div>
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <p className='text-darker/40 text-sm'>Bookings This Month</p>
                         <p className='text-darker text-3xl font-bold mt-1'>
                              {stats.monthlyBookings}
                         </p>
                    </div>
                    <div className='bg-white rounded-xl p-5 border border-gray-200'>
                         <p className='text-darker/40 text-sm'>Revenue This Month</p>
                         <p className='text-yellow text-3xl font-bold mt-1'>
                              ${(stats.monthlyRevenue / 100).toLocaleString()}
                         </p>
                    </div>
               </div>

               {/* Away Mode */}
               <div
                    className={`rounded-xl border-2 p-5 mb-8 duration-300 ${
                         storeOffline
                              ? 'border-yellow bg-yellow/5'
                              : 'border-gray-200 bg-white'
                    }`}
               >
                    <div className='flex items-start justify-between gap-4 flex-wrap'>
                         <div>
                              <h2 className='text-darker text-lg font-bold'>
                                   Away Mode
                              </h2>
                              <p className='text-darker/60 text-sm mt-1'>
                                   When enabled, the public Pricing and Schedule pages
                                   show your custom message and block new bookings.
                              </p>
                         </div>
                         <label className='inline-flex items-center gap-3 cursor-pointer'>
                              <span className='text-darker font-semibold text-sm'>
                                   {storeOffline ? 'Closed' : 'Open'}
                              </span>
                              <span className='relative inline-block w-12 h-6'>
                                   <input
                                        type='checkbox'
                                        checked={storeOffline}
                                        onChange={(e) => {
                                             const next = e.target.checked;
                                             setStoreOffline(next);
                                             saveStoreStatus(next, offlineMessage);
                                        }}
                                        disabled={savingStatus}
                                        className='peer sr-only'
                                   />
                                   <span className='absolute inset-0 rounded-full bg-gray-300 peer-checked:bg-yellow duration-200' />
                                   <span className='absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow duration-200 peer-checked:translate-x-6' />
                              </span>
                         </label>
                    </div>

                    <div className='mt-4'>
                         <label className='block text-darker/60 text-sm mb-1'>
                              Message shown to visitors
                         </label>
                         <textarea
                              value={offlineMessage}
                              onChange={(e) => setOfflineMessage(e.target.value)}
                              onBlur={() => saveStoreStatus(storeOffline, offlineMessage)}
                              rows={3}
                              placeholder={DEFAULT_OFFLINE_MESSAGE}
                              className='w-full bg-white border border-gray-200 rounded-lg px-4 py-3 text-darker placeholder-darker/30 focus:outline-none focus:border-yellow duration-300'
                         />
                         <p className='text-darker/40 text-xs mt-1'>
                              Message saves automatically when you click out of the box.
                              {statusSavedAt && (
                                   <span className='text-green-600 ml-2'>
                                        ✓ Saved {format(statusSavedAt, 'p')}
                                   </span>
                              )}
                         </p>
                         {statusError && (
                              <p className='text-red-700 text-sm mt-2'>{statusError}</p>
                         )}
                    </div>
               </div>

               {/* Upcoming Bookings */}
               <div className='bg-white rounded-xl border border-gray-200'>
                    <div className='p-4 border-b border-gray-200 flex items-center justify-between'>
                         <h2 className='text-darker text-lg'>Upcoming Bookings</h2>
                         <Link
                              to='/admin/bookings'
                              className='text-yellow text-sm hover:underline'
                         >
                              View All →
                         </Link>
                    </div>
                    {stats.upcoming.length === 0 ? (
                         <p className='text-darker/40 p-4'>
                              No upcoming bookings in the next 7 days.
                         </p>
                    ) : (
                         <div className='divide-y divide-gray-200'>
                              {stats.upcoming.map((booking) => (
                                   <Link
                                        key={booking.id}
                                        to={`/admin/bookings/${booking.id}`}
                                        className='flex items-center justify-between p-4 hover:bg-gray-100 duration-300'
                                   >
                                        <div>
                                             <p className='text-darker'>
                                                  {booking.customer_name}
                                             </p>
                                             <p className='text-darker/40 text-sm'>
                                                  {format(new Date(booking.start_date), 'MMM d')} –{' '}
                                                  {format(new Date(booking.end_date), 'MMM d, yyyy')}
                                             </p>
                                        </div>
                                        <span className='text-yellow font-bold'>
                                             ${(booking.total_cents / 100).toLocaleString()}
                                        </span>
                                   </Link>
                              ))}
                         </div>
                    )}
               </div>
          </div>
     );
}
